import { env } from "./env";

declare global {
  interface Window {
    twq?: TwqFunction;
  }
}

interface TwqFunction {
  (...args: unknown[]): void;
  exe?: (...args: unknown[]) => void;
  queue: unknown[][];
  version: string;
}

const SIGNUP_ALREADY_FIRED_KEY = "x-pixel-signup-fired";

let initialized = false;

/**
 * Loads X's (Twitter's) universal website tag and configures it for our pixel, once. A no-op
 * entirely when `VITE_X_PIXEL_ID` isn't set — the pixel doesn't exist yet, or this is local dev —
 * so this is always safe to call unconditionally at startup. The base tag automatically fires a
 * PageView on this initial load, which is what actually matters for ad-click attribution (someone
 * clicking an X ad lands on a fresh page load, not an in-app client-side navigation); this does
 * not additionally re-fire on every SPA route change.
 */
export function initXPixel(): void {
  if (initialized || !env.VITE_X_PIXEL_ID) return;
  initialized = true;

  // X's own loader boilerplate (documented at business.x.com's conversion-tracking setup flow),
  // adapted to TypeScript. Defines `window.twq` as a queueing shim, then loads the real script
  // async — calls made before it loads are queued and replayed once it's ready.
  const w = window;
  if (!w.twq) {
    const twq: TwqFunction = (...args: unknown[]) => {
      (twq.exe ? twq.exe : (a: unknown[]) => twq.queue.push(a))(args);
    };
    twq.version = "1.1";
    twq.queue = [];
    w.twq = twq;
  }
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://static.ads-twitter.com/uwt.js";
  const firstScript = document.getElementsByTagName("script")[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);

  w.twq("config", env.VITE_X_PIXEL_ID);
}

/**
 * Fires the "Page view" conversion event — separate from (and in addition to) the base tag's own
 * automatic pageview-on-load, since that automatic signal doesn't apply the attribution windows
 * configured on a named Events Manager event, and doesn't fire at all on a client-side SPA route
 * change. Call this once on initial load and again on every route change (see AppShell.tsx). A
 * no-op when `VITE_X_PAGEVIEW_EVENT_ID` isn't configured.
 */
export function trackPageView(): void {
  if (!env.VITE_X_PAGEVIEW_EVENT_ID) return;
  window.twq?.("event", env.VITE_X_PAGEVIEW_EVENT_ID, {});
}

/**
 * Fires the "Sign up" (Lead) conversion event exactly once per browser (guarded by localStorage,
 * which survives the page reload that follows sign-in) — call this whenever
 * `AuthPrincipal.isNewUser` is true. `emailHash` is the account's own email, already one-way
 * SHA-256-hashed server-side (see services/identity.ts) — the raw email is never available here
 * to begin with, so there's nothing to hash or mishandle client-side. Passed through as X's
 * `email_address` Enhanced/Advanced Matching parameter. A no-op when `VITE_X_SIGNUP_EVENT_ID`
 * isn't configured.
 */
export function trackSignUp(emailHash: string | null): void {
  if (!env.VITE_X_SIGNUP_EVENT_ID) return;
  try {
    if (localStorage.getItem(SIGNUP_ALREADY_FIRED_KEY)) return;
    localStorage.setItem(SIGNUP_ALREADY_FIRED_KEY, "1");
  } catch {
    // Private browsing / storage disabled — fall through and fire anyway rather than silently
    // dropping a real conversion; worst case is a rare duplicate, not a missing one.
  }
  window.twq?.("event", env.VITE_X_SIGNUP_EVENT_ID, {
    ...(emailHash ? { email_address: emailHash } : {}),
  });
}
